/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  UIEvent,
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import debounce from 'lodash.debounce';

import { withTranslation } from 'react-i18next';

import Autocomplete from '@mui/material/Autocomplete';

import { CustomPopper } from './CustomPopper/CustomPopper';
// commented to keep the scroll in the select.
/* import { CustomNoElevationPaper } from './CustomPaper/CustomNoElevationPaper';
import { CustomElevationPaper } from './CustomPaper/CustomElevationPaper'; */
import AutocompleteList from './AutocompleteList/AutocompleteList';
import AutocompleteTextField from './AutocompleteTextField/AutocompleteTextField';
import ObrasAPI from '../../../../../services/APIObras';
import { TFunction } from 'i18next';
import { FilterContext } from '../../../../../context/FilterProvider';
import { Mode } from '../../../../../types/FiltersTypes';
import FILTER_TYPES from '../../../filterTypes';
import { filterMeaningfulOptions } from '../../../../../utils/filterMeaninglessOptions';
import { useCloseOnScroll } from '../../../../../utils/useCloseOnScroll';
import type { AutocompleteOption, AutocompleteOptionsSource } from './AutocompleteOptionsSource';

const fieldsNotLanguage = ['namePartner'];

type Option = AutocompleteOption;
interface CustomAutocompleteProps {
  filterType: string,
  disableScroll: (disable: boolean) => void,
  optionsSource?: AutocompleteOptionsSource,
  t: TFunction<'translations'>;
}
const CustomAutocomplete = (
  props: CustomAutocompleteProps,
) => {
  const { t, filterType, disableScroll, optionsSource } = props;
  const filterContext = useContext(FilterContext);
  const tags = filterContext.getTags(filterType);

  const NO_RESULTS = [{ name: `${t('input.noResults')}` }];

  const minCharacters = optionsSource?.minQueryLength ?? 3;

  const [options, setOptions] = useState<Array<Option>>([]);
  const [loading, setLoading] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [optionsPage, setOptionsPage] = useState(0);
  const [hasNextOptionsPage, setHasNextOptionsPage] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  useCloseOnScroll(openModal, setOpenModal, anchorRef);

  const fetchOptionsFromSource = async (text: string, page = 0, append = false) => {
    if (!optionsSource) return;
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    setLoading(true);
    if (!append) setOptions([]);

    try {
      const response = await optionsSource.search(text, page, optionsSource.pageSize);
      if (requestId.current !== currentRequestId) return;

      setOptionsPage(page);
      setHasNextOptionsPage(response.hasNextPage);
      setOptions((currentOptions) => {
        if (!append) return response.options.length ? response.options : NO_RESULTS;
        const existingCodes = new Set(currentOptions.map((option) => option.code));
        return currentOptions.concat(response.options.filter((option) => {
          if (existingCodes.has(option.code)) return false;
          existingCodes.add(option.code);
          return true;
        }));
      });
    } catch (error) {
      if (requestId.current !== currentRequestId) return;
      console.log(error);
      if (!append) setOptions(NO_RESULTS);
      setHasNextOptionsPage(false);
    } finally {
      if (requestId.current === currentRequestId) setLoading(false);
    }
  };

  const handleListboxScroll = (event: UIEvent<HTMLUListElement>) => {
    if (!optionsSource || !hasNextOptionsPage || loading) return;
    const listboxNode = event.currentTarget;
    const isNearBottom = listboxNode.scrollTop + listboxNode.clientHeight
      >= listboxNode.scrollHeight - 48;
    if (isNearBottom && textFieldValue.trim().length >= minCharacters) {
      void fetchOptionsFromSource(textFieldValue.trim(), optionsPage + 1, true);
    }
  };

  const handleSearchTextChange = () => {
    requestId.current += 1;
    setLoading(false);
    setOptions([]);
    setOptionsPage(0);
    setHasNextOptionsPage(false);
  };

  const inputRequest = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = e.target.value.trim();
    if (searchText.length >= minCharacters) {
      if (optionsSource) {
        await fetchOptionsFromSource(searchText);
        return;
      }
      setLoading(true);
      setOptions([]);
      const notLanguage = fieldsNotLanguage.some((e: string) => e === filterType);
      const currentRequestId = requestId.current;

      try {
        const api = ObrasAPI.getInstance();
        let results;
        if ([
          FILTER_TYPES.supercenter,
          FILTER_TYPES.zone,
        ].includes(filterType))
          results = await api.getAutocompleteMastersUrl(filterType, searchText);
        else
          results = await api.getAutocompleteUrl(filterType, searchText, notLanguage);
        if (requestId.current !== currentRequestId) return;
        results = filterMeaningfulOptions(results as Option[]);
        results = results.length ? results : NO_RESULTS;
        return setOptions(results);
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId.current === currentRequestId) setLoading(false);
      }
    }
    if (optionsSource) {
      requestId.current += 1;
      setOptionsPage(0);
      setHasNextOptionsPage(false);
    }
    setLoading(false);
    return setOptions([]);
  };

  const debounceInputHandler = useCallback(
    debounce(inputRequest, 500),
    [],
  );

  const shouldOpenAutocomplete = openModal && (
    !optionsSource
    || (
      textFieldValue.trim().length >= minCharacters
      && (loading || options.length > 0)
    )
  );

  return (
    <Autocomplete
      ref={anchorRef}
      size="medium"
      multiple
      freeSolo={optionsSource ? optionsSource.allowFreeSolo : true}
      open={shouldOpenAutocomplete}
      renderTags={() => null}
      disableCloseOnSelect
      disableClearable
      data-testid={t(`filters.${filterType}`)}
      id={`${t(`filters.${filterType}`)}`}
      inputValue={textFieldValue}
      options={options}
      loading={loading}
      noOptionsText={optionsSource && options.length === 0 ? ' ' : undefined}
      ListboxProps={optionsSource ? { onScroll: handleListboxScroll } : undefined}
      loadingText={t('input.loading')}
      getOptionLabel={(option: any) => option.name}
      isOptionEqualToValue={(option, valueToEqual) => option.name === valueToEqual.name}
      filterOptions={(option) => option}
      PopperComponent={CustomPopper}
      getOptionDisabled={
        (option: Option) => (option.name === t('input.noResults'))
      }
      style={
        {
          padding: '0.75rem',
        }
      }
      value={tags}
      onChange={(e: any, newFilter: readonly any[]) => {
        const selectedCode = e.target.id;
        const filterValue = newFilter.find((f) => f.code === selectedCode);
        const modeFilter = !filterValue ? Mode.DELETE : Mode.ADD;
        filterContext.handleChange(filterType, filterValue || { code: selectedCode }, modeFilter);

      }}
      onOpen={() => {
        disableScroll(true);
      }}
      onClose={() => {
        disableScroll(false);
      }}
      onBlur={() => setOpenModal(false)}
      onFocus={() => setOpenModal(true)}
      // Commented to keep the scroll in the select.
      // PaperComponent={(options.length > 0) ? (props) => (<CustomElevationPaper {...props} id={`${t(`filters.${filterType}`)}`} />) : CustomNoElevationPaper}
      renderOption={(props, option: any) => (
        <AutocompleteList
          {...props}
          key={option.code || uuidv4()}
          props={props}
          option={option}
          selected={!!tags.some((o) => o.code === option.code)}
          clickOption={() => {
            if(option.name === t('input.noResults')) return;
            const selected = !!tags.some((o) => o.code === option.code);
            const modeFilter = selected ? Mode.DELETE : Mode.ADD;
            filterContext.handleChange(filterType, { name: option.name, code: option.code }, modeFilter);
          }}
        />
      )
      }
      renderInput={(params) => (
        <AutocompleteTextField
          filterType={filterType}
          params={params}
          debounceInputHandler={debounceInputHandler}
          textFieldValue={textFieldValue}
          setTextFieldValue={setTextFieldValue}
          setOptions={setOptions}
          loading={loading}
          onTextChange={handleSearchTextChange}
        />
      )}
    />
  );
};

export default withTranslation()(CustomAutocomplete);
